<template>
    <article class="f fv pr fns dw-10 br-m el-frm-datepicker-wrapper"
        :class="[
            !uiShowHeader ? 'el--standalone' : '',
            !uiAdjustable ? 'el--disabled' : '',
            !ui.is_collapsed ? 'el--open' : '',
            'el--align-' + uiAlign,
        ]">
        <header class="f fh fa-c sp-l-s sp-r-xs el-frm-datepicker-input" v-if="uiShowHeader" @click="toggleCollapse()">
            <h1 class="fg">
                {{ athDate((cursor || {}).date, uiFormat) }}
            </h1>
            <AthButtonIcon class="fns el--s-xs"
                icon="calendar-31"
                :disabled="!uiAdjustable"
                v-if="uiCollapsible" />
        </header>
        <div class="el-frm-datepicker" :class="[ui.is_collapsed ? 'u-hide' : '']" v-if="uiAdjustable">
            <!-- Controls -->
            <nav class="f fh fa-c sm-b-s el-frm-datepicker-controls">
                <AthButtonIcon class="fns"
                    icon="chevron-left"
                    :disabled="!ui.past_enabled"
                    @click.prevent.stop="previousPeriod()" />
                <span class="fg ta-c el--interactive" @click="setMode('year')" v-if="meta.mode === 'month'">
                    {{ athDate(meta.base, "MMMM YYYY") }}
                </span>
                <span class="fg ta-c" v-else-if="meta.mode === 'year'">
                    {{ athDate(meta.base, "YYYY") }}
                </span>
                <AthButtonIcon class="fns"
                    icon="chevron-right"
                    :disabled="!ui.future_enabled"
                    @click.prevent.stop="nextPeriod()" />
            </nav>
            <!-- Content -->
            <div class="el-frm-datepicker-main">
                <aside class="f fh fa-c fj-sa el-frm-datepicker-legend" v-if="meta.mode === 'month'">
                    <span class="ta-c te-fat-section" v-for="(day, ix) in meta.days" :key="'header'+ix">
                        {{ day }}
                    </span>
                </aside>
                <div class="f fv el-frm-datepicker-period">
                    <template v-if="meta.mode === 'month'">
                        <section class="f fh fj-sa"
                            v-for="(row, ix) in meta.list"
                            :key="'month'+ix">
                            <AthButton v-for="(obj, ix2) in row"
                                :key="'month'+ix2"
                                class="fg el--transparent el-frm-datepicker-period-cell el--day"
                                :lbl="athDate(obj.date, 'D')"
                                :class="[
                                    !obj.is_same_month ? 'el--outside' : '',
                                    (poi[obj.poi_hash] || []).length > 0 ? 'el--poi' : '',
                                    obj.is_selected ? 'el--active' : '',
                                ]"
                                :disabled="obj.is_disabled"
                                @click.prevent.stop="setDay(obj)" />
                        </section>
                    </template>
                    <template v-else-if="meta.mode === 'year'">
                        <section class="f fh fj-sa"
                            v-for="(row, ix) in meta.list"
                            :key="'year'+ix">
                            <AthButton v-for="(obj, ix2) in row"
                                :key="'year'+ix2"
                                class="fg el--transparent el-frm-datepicker-period-cell el--month"
                                :lbl="athDate(obj.date, 'MMM')"
                                :class="[obj.is_selected ? 'el--active' : '']"
                                :disabled="obj.is_disabled"
                                @click.prevent.stop="setMonth(obj)" />
                        </section>
                    </template>
                </div>
            </div>
        </div>
    </article>
</template>

<script>
import { isObject, isString, sleep } from '@aretheyhappy/utils';

import dayjs                from 'dayjs';
import weekday              from 'dayjs/plugin/weekday';
import isoWeek              from 'dayjs/plugin/isoWeek';
import localeData           from 'dayjs/plugin/localeData';
import customParseFormat    from 'dayjs/plugin/customParseFormat';
import advancedFormat       from 'dayjs/plugin/advancedFormat';
import pluralGetSet         from 'dayjs/plugin/pluralGetSet';
import timezone             from 'dayjs/plugin/timezone';
import utc                  from 'dayjs/plugin/utc';
import localizedFormat      from 'dayjs/plugin/localizedFormat';
import updateLocale         from 'dayjs/plugin/updateLocale';
import AthButtonIcon from '../ButtonIcon.vue';
import AthButton from '../Button.vue';

// DayJS Plugins
dayjs.extend(weekday);
dayjs.extend(isoWeek);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(timezone);
dayjs.extend(pluralGetSet);
dayjs.extend(utc);
dayjs.extend(localizedFormat);
dayjs.extend(updateLocale);

// DayJS Locales
import 'dayjs/locale/nl';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/fr';

export default {
    name: 'mead-form-date-picker',
    components: {AthButton, AthButtonIcon},
    props: {
        /**
         * Currently selected date, as a 'YYYY-MM-DD' string (v-model).
         */
        value           : {type: String, default: () => ''},
        /**
         * Map of "points of interest" keyed by a per-day hash (`YYYY-MM-DD`), used to render a dot
         * indicator on days that have one or more associated entries.
         */
        poi             : {type: Object, default: () => ({})},
        /**
         * Whether the calendar can be opened and interacted with. When false, the header toggle
         * (and collapse icon) are disabled and the calendar body is not rendered at all.
         */
        uiAdjustable    : {type: Boolean, default: () => true},
        /**
         * Automatically opens the calendar on mount (only takes effect when `uiShowHeader` is false).
         */
        uiAutoOpen      : {type: Boolean, default: () => false},
        /**
         * Automatically collapses/closes the calendar after a day is selected.
         */
        uiAutoclose     : {type: Boolean, default: () => false},
        /**
         * Whether to show the header displaying the currently selected date and the collapse toggle.
         */
        uiShowHeader    : {type: Boolean, default: () => true},
        /**
         * Whether the calendar can be collapsed/expanded by clicking the header.
         */
        uiCollapsible   : {type: Boolean, default: () => true},
        /**
         * Allows deselecting the currently selected day by clicking it again.
         */
        uiAllowDeselect : {type: Boolean, default: () => false},
        /**
         * Alignment of the calendar popover relative to the header (e.g. 'br', 'tl', 't', 'b').
         */
        uiAlign         : {type: String, default: () => 'br'},
        /**
         * Format string used to display the selected date in the header.
         */
        uiFormat        : {type: String, default: () => 'ddd D MMM YYYY'},
        /**
         * Whether dates before today are selectable. Also used to determine the initial cursor
         * date and whether navigating to the previous period is allowed.
         */
        pastEnabled     : {type: Boolean, default: () => true},
        /**
         * Whether dates after today are selectable.
         */
        futureEnabled   : {type: Boolean, default: () => true},
        /**
         * Whether today's date is selectable.
         */
        todayEnabled    : {type: Boolean, default: () => true},
        /**
         * Earliest selectable date, as a 'YYYY-MM-DD' string; dates before this are disabled.
         */
        notBefore       : {type: [Boolean, String], default: () => false},
        /**
         * Latest selectable date, as a 'YYYY-MM-DD' string; dates after this are disabled.
         */
        notAfter        : {type: [Boolean, String], default: () => false},
    },
    data () {
        return {
            cursor : false,
            meta: {
                now         : this.getInstance(MeadCommon.I18N.lng),
                mode        : 'month',
                days        : [1, 2, 3, 4, 5, 6, 7].map(day => dayjs().isoWeekday(day).format('ddd')),
                list        : [],
                base        : false,
                not_before  : isString(this.notBefore) ? this.getInstance(MeadCommon.I18N.lng, this.notBefore, 'YYYY-MM-DD') : false,
                not_after   : isString(this.notAfter) ? this.getInstance(MeadCommon.I18N.lng, this.notAfter, 'YYYY-MM-DD') : false,
            },
            ui: {
                is_collapsed    : !!this.uiCollapsible,
                future_enabled  : false,
                past_enabled    : false,
            },
        };
    },
    methods: {
        athDate: function (val, format = false, original_format = false) {
            if (!val) return '';
            if (original_format) {
                return dayjs(val, original_format).locale(MeadCommon.I18N.lng).format(format || 'll HH:mm');
            } else {
                return dayjs(val).locale(MeadCommon.I18N.lng).format(format || 'll HH:mm');
            }
        },
        /**
         * Get instance of DayJS plugin
         * @param {string} lang - Locale for the date picker
         * @param {Date} value - Date object
         * @param {string} format - Format structure (ex: 'YYYY-MM-DD')
         * @returns
         */
        getInstance: function (lang = 'en', value, format) {
            let instance;
            dayjs.locale(lang);
            dayjs.updateLocale(lang, {
                weekStart: 1,
            });

            if (value) {
                instance = format ? dayjs(value, format) : dayjs(value);
            } else {
                instance = dayjs();
            }

            return instance;
        },
        //  UTIL

        //  Reset active cursor
        reset: function () {
            this.dayjsInstance = this.getInstance(MeadCommon.I18N.lng);
            if (this.todayEnabled) {
                this.meta.base = this.dayjsInstance.clone().hours(12);
            } else if (this.pastEnabled) {
                this.meta.base = this.dayjsInstance.clone().hours(12).add(-1, 'day');
            } else if (this.futureEnabled) {
                this.meta.base = this.dayjsInstance.clone().hours(12).add(1, 'day');
            }

            if (this.cursor) this.cursor.is_selected = false;
            this.cursor = false;
        },
        //  Check if a date is disabled based on the passed config
        isDisabled: function (m, check) {
            //  Past check
            if (!this.pastEnabled && m.isBefore(this.dayjsInstance.startOf(check), check)) return true;

            //  Future check
            if (!this.futureEnabled && m.isAfter(this.dayjsInstance.endOf(check), check)) return true;

            //  Today check
            if (check === 'day' && !this.todayEnabled && m.isSame(this.meta.now, check)) return true;

            //  Not before check
            if (this.meta.not_before &&
                m.isBefore(this.getInstance(MeadCommon.I18N.lng, this.meta.not_before).startOf(check), check)
            ) return true;

            //  Not after check
            if (this.meta.not_after
                && m.isAfter(this.getInstance(MeadCommon.I18N.lng, this.meta.not_after).endOf(check), check)) return true;

            return false;
        },
        //  Create a model of a day to be used in the ui
        M_Day: function (dayjs_obj, is_selected = false) {
            return {
                date            : dayjs_obj.clone(),
                is_same_month   : dayjs_obj.isSame(this.meta.base, 'month'),
                is_today        : dayjs_obj.isSame(this.meta.now, 'day'),
                is_selected     : is_selected,
                is_disabled     : this.isDisabled(dayjs_obj, 'day'),
            };
        },
        //  Create a model of a month to be used in the ui
        M_Month: function (dayjs_obj, is_selected = false) {
            return {
                date            : dayjs_obj.clone(),
                is_same_month   : dayjs_obj.isSame(this.meta.base, 'month'),
                is_selected     : is_selected,
                is_disabled     : this.isDisabled(dayjs_obj, 'month'),
            };
        },

        //  UI

        notify: function (dayjs_obj) {
            //  Check if valid, otherwise emit false
            if (!dayjs_obj || !dayjs.isDayjs(dayjs_obj)) {
                return this.$emit('input', false);
            }

            //  Everything checks out!
            this.$emit('input', dayjs_obj.format('YYYY-MM-DD'));
        },


        render: function () {
            if (this.meta.mode === 'month') {
                this.meta.list = [];

                let day_of_week         = 0;
                let cursor_week_index   = -1;
                let cursor              = this.getInstance(MeadCommon.I18N.lng, this.meta.base).startOf('month').startOf('week').hour(12);
                const end               = this.getInstance(MeadCommon.I18N.lng, this.meta.base).endOf('month').endOf('week');

                //  Build list of days for current month
                while (cursor.isBefore(end)) {
                    const day = this.M_Day(cursor);

                    //  Next week, increment index
                    if (day_of_week % 7 === 0) {
                        this.meta.list.push([]);
                        cursor_week_index++;
                    }

                    //  If we have a cursor, do some other checks
                    if (this.cursor) {
                        //  Check if cursor is the same day
                        if (cursor.isSame(this.cursor.date, 'day')) {
                            day.is_selected = true;
                            this.cursor = day;
                        }
                    }

                    day.poi_hash = cursor.format('YYYY-MM-DD');
                    this.meta.list[cursor_week_index].push(day);
                    day_of_week++;
                    cursor = cursor.add(1, 'day');
                }

                //  Are past months enabled ?
                this.ui.past_enabled = this.pastEnabled || !this.meta.base.clone().add(-1, 'month').isBefore(this.meta.now, 'month');

                //  Are future months enabled ?
                this.ui.future_enabled = this.futureEnabled || !this.meta.base.clone().add(1, 'month').isAfter(this.meta.now, 'month');
            } else {
                this.meta.list = [];

                let cursor      = this.meta.base.clone().startOf('month').startOf('year').hour(12);
                const end       = this.meta.base.clone().endOf('month').endOf('year');

                //  Build list of days for current month
                let row_ix = -1;
                let month_count = -1;
                while (cursor.isBefore(end)) {
                    const month = this.M_Month(cursor);
                    month_count += 1;

                    //  Next quarter, increment index
                    if (month_count % 3 === 0) {
                        this.meta.list.push([]);
                        row_ix++;
                    }

                    //  If we have a cursor, do some other checks
                    if (this.cursor) {
                        //  Check if cursor is the same day
                        if (cursor.isSame(this.cursor.date, 'month')) {
                            month.is_selected = true;
                        }
                    }

                    this.meta.list[row_ix].push(month);
                    cursor = cursor.add(1, 'month');
                }

                //  Are past months enabled ?
                this.ui.past_enabled = this.pastEnabled || !this.meta.base.clone().add(-1, 'year').isBefore(this.meta.now, 'year');

                //  Are future months enabled ?
                this.ui.future_enabled = this.futureEnabled || !this.meta.base.clone().add(1, 'year').isAfter(this.meta.now, 'year');
            }
        },
        documentClickHandler: function (evt) {
            if (this.$el.contains(evt.target)) {
                MeadCommon.DomUtils.preventImmediateEventEscape(evt);
                return;
            }
            this.close();
        },
        close: function () {
            this.ui.is_collapsed = true;
            document.removeEventListener('click', this.documentClickHandler);
            this.$emit('close');
        },
        open: async function () {
            this.ui.is_collapsed = false;
            await sleep(50);
            document.addEventListener('click', this.documentClickHandler);
        },
        //  Sets the current mode (eg: 'month', 'year') for rendering
        setMode: function (mode = 'month') {
            this.meta.mode = mode;
            this.render();
        },
        //  Collapse toggle
        toggleCollapse: function () {
            if (!this.uiAdjustable) return;
            if (this.ui.is_collapsed) this.open();
            else this.close();
        },
        //  Select the passed day as the new cursor
        setDay: function (day) {
            if (!this.cursor) {
                this.cursor = day;
            } else if (this.cursor.date.isSame(day.date, 'day')) {
                //  Deselect if same day and allow deselect
                if (this.uiAllowDeselect) {
                    day.is_selected = !day.is_selected;
                    this.cursor = day.is_selected ? day : false;
                }
            } else {
                //  Deselect active day
                this.cursor.is_selected = false;

                //  Set active day
                this.cursor = day;
                this.cursor.is_selected = true;
            }

            //  Emit
            this.notify((this.cursor || {}).date || false);

            //  Automatically close if autoclose is set
            if (this.uiAutoclose) this.close();
        },
        //  Select the passed day as the new cursor
        setMonth: function (obj) {
            this.meta.base = this.meta.base.clone().set('month', obj.date.month());
            this.meta.mode = 'month';
            this.render();
        },
        //  Decrement period by 1 and re-render
        previousPeriod: function () {
            this.meta.base = this.meta.base.clone().add(-1, this.meta.mode);
            this.render();
        },
        //  Increment period by 1 and re-render
        nextPeriod: function () {
            this.meta.base = this.meta.base.clone().add(1, this.meta.mode);
            this.render();
        },
        //  Set cursor & render calendar
        init: function () {
            //  Set cursor
            if (this.value && this.value !== '') {
                this.meta.base = this.getInstance(MeadCommon.I18N.lng, this.value, 'YYYY-MM-DD').startOf('day');
            }

            this.cursor = this.M_Day(this.meta.base.clone(), true);
            this.meta.base = this.meta.base.add(0, 'month');

            this.render();

            //  If value was not set send a notification
            if (this.value === false || this.value === '') {
                this.notify(this.cursor.date);
            }
        },
    },
    watch: {
        notBefore: function () {
            this.meta.not_before = isString(this.notBefore)
                ? this.getInstance(MeadCommon.I18N.lng, this.notBefore, 'YYYY-MM-DD')
                : false;

            if (!this.meta.not_before || !this.cursor || !this.cursor.date) return;

            //  If the current cursor is before this date, autoselect the notbefore as the new cursor
            if (this.cursor.date.isBefore(this.meta.not_before)) {
                this.cursor     = this.M_Day(this.meta.not_before.clone().add(1, 'days'), true);
                this.meta.base  = this.meta.not_before.clone();
                this.render();
                this.notify(this.cursor.date);
            } else {
                this.render();
            }
        },
        notAfter: function () {
            this.meta.not_after = isString(this.notAfter)
                ? this.getInstance(MeadCommon.I18N.lng, this.notAfter, 'YYYY-MM-DD').hours(12)
                : false;

            if (!this.meta.not_after || !this.cursor || !this.cursor.date) return;

            //  If the current cursor is after this date, autoselect the notafter as the new cursor
            if (this.cursor.date.isAfter(this.meta.not_after)) {
                this.cursor     = this.M_Day(this.meta.not_after.clone().add(-1, 'days'), true);
                this.meta.base  = this.meta.not_after.clone();
                this.render();
                this.notify(this.cursor.date);
            } else {
                this.render();
            }
        },
        value: function (old_val, new_val) {
            const old_val_str = isObject(old_val) ? old_val.format() : old_val;
            const new_val_str = isObject(new_val) ? new_val.format() : new_val;
            if (old_val_str === new_val_str) return;
            this.init();
        },
    },
    mounted () {
        this.reset();
        this.init();

        if (!this.uiShowHeader && this.uiAutoOpen) this.open();
    },
};
</script>

<style lang="scss" scoped>
@use '../../../../sass/variables' as *;

.el-frm-datepicker-wrapper {
    width: auto;

    * {
        user-select: none;
    }

    //  Modifiers

    &:not(.el--standalone) {
        .el-frm-datepicker-input {
            padding-left: 0;
            padding-right: 0;
            border-bottom: 1px solid var(--c-frm-border);
        }
    }

    &.el--standalone {
        .el-frm-datepicker-input {
            height: 4rem;
            border-radius: 1rem;
            background-color: var(--c-brand-light-bg);
        }

        &:not(.el--disabled):not(.el--active) .el-frm-datepicker-input:hover {
            background-color: var(--c-brand-default-bg);
        }
    }

    &.el--active .el-frm-datepicker-input {
        &,
        & > button {
            background-color: var(--c-brand-active-bg);
            color: var(--c-brand-active-fg);
        }
    }

    &.el--disabled .el-frm-datepicker-input {
        cursor: not-allowed;
    }

    &.el--open {
        position: relative;
        z-index: 10;
    }

    //  Alignment Modifiers

    &.el--align-b .el-frm-datepicker {
        top: calc(100% + 1rem);
    }

    &.el--align-t .el-frm-datepicker {
        bottom: calc(100% + 1rem);
    }

    &.el--align-br .el-frm-datepicker {
        top: calc(100% + 1rem);
        right: 0;
    }

    &.el--align-tr .el-frm-datepicker {
        bottom: calc(100% + 1rem);
        right: 0;
    }

    &.el--align-bl .el-frm-datepicker {
        top: calc(100% + 1rem);
        left: 0;
    }

    &.el--align-tl .el-frm-datepicker {
        bottom: calc(100% + 1rem);
        left: 0;
    }
}

.el-frm-datepicker-input {
    line-height: 4rem;
    cursor: pointer;

    h1 {
        font-size: $fs-m;
        line-height: 4rem;
        font-weight: 100;
        white-space: nowrap;
    }
}

.el-frm-datepicker {
    width: 28rem;
    min-height: 27.2rem;
    box-shadow: var(--c-shade-l);
    position: absolute;
    background-color: var(--c-panel-bg);
    border: 1px solid var(--c-panel-border);
    border-radius: $br-m;
    padding: $s-s;
}

.el-frm-datepicker-controls > span {
    font-weight: bold;
    font-size: $fs-m;
    line-height: 3rem;

    &.el--interactive {
        border-radius: $br-m;
        cursor: pointer;
        padding: 0 $s-xs;
        margin: 0 $s-xs;

        &:hover {
            background-color: var(--c-brand-light-bg);
            color: var(--c-brand-light-fg);
        }
    }
}

.el-frm-datepicker-legend > span {
    display: block;
    width: 4rem;
    height: 3rem;
}

.el-frm-datepicker-period-cell {
    padding: 0;
    font-size: $fs-s;
    position: relative;
    font-weight: 400;
    border-radius: $br-m;

    &.el--day {
        height: 3rem;
        line-height: 3rem;
    }

    &.el--month {
        height: 5rem;
        line-height: 5rem;
    }

    &:hover {
        background-color: var(--c-brand-default-bg);
    }

    &[disabled="disabled"] {
        opacity: .2;
    }

    //  Modifiers

    &.el--outside:not([disabled="disabled"]):not(.el--active) {
        color: var(--c-txt-light);
        opacity: .7;
    }

    &.el--active {
        background-color: var(--c-brand-active-bg);
        color: var(--c-brand-active-fg);
        font-weight: bold;
    }

    &.el--poi:after {
        display: inline-block;
        width: 1rem;
        height: 1rem;
        border-radius: 50%;
        content: '';
        background-color: var(--c-brand-dark-bg);
        position: absolute;
        bottom: 0;
        right: 0;
    }
}
</style>
